import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Alert, Form, Button } from "react-bootstrap";
import DynamicTable from "../../components/DynamicTable";
import LivreurDetailModal from "./LivreurDetailModal";
import { fetchAllPerformance } from "./DataService";

const CA_OBJECTIF = 5000;

const fmtEuros = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v ?? 0);

const fmtHeures = (h) => {
  const hh = Math.floor(h ?? 0);
  const mm = Math.round(((h ?? 0) - hh) * 60);
  return `${hh}h${mm > 0 ? String(mm).padStart(2, "0") : ""}`;
};

function KpiCard({ label, value, sub, highlight }) {
  return (
    <Card
      className="text-center h-100"
      style={{ borderColor: highlight ? "#28a745" : undefined }}
    >
      <Card.Body className="py-3">
        <div className="text-muted small mb-1">{label}</div>
        <div className="fw-bold fs-5">{value}</div>
        {sub && <div className="text-muted small mt-1">{sub}</div>}
      </Card.Body>
    </Card>
  );
}

function LivreursPerformance() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState(null);

  const fetchData = async (m) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAllPerformance(m);
      setData(res);
    } catch {
      setError("Impossible de charger les données.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(month);
  }, [month]);

  const columns = useMemo(
    () => [
      {
        Header: "Livreur",
        accessor: "livreur",
        Cell: ({ value }) => `${value.prenom} ${value.nom}`,
      },
      {
        Header: "Shifts",
        accessor: "nb_shifts",
      },
      {
        Header: "Heures",
        accessor: "heures",
        Cell: ({ value }) => fmtHeures(value),
      },
      {
        Header: "Km",
        accessor: "km",
        Cell: ({ value }) => `${value} km`,
      },
      {
        Header: "Livraisons",
        accessor: "nb_livraisons",
      },
      {
        Header: "CA TTC",
        accessor: "ca_ttc",
        Cell: ({ value }) => fmtEuros(value),
      },
      {
        Header: "CA/h",
        accessor: "ca_par_heure",
        Cell: ({ value }) => fmtEuros(value),
      },
      {
        Header: "Objectif 5K",
        accessor: "objectif_atteint",
        Cell: ({ value }) =>
          value ? (
            <span style={{ color: "#28a745", fontWeight: "bold" }}>✅ Atteint</span>
          ) : (
            <span style={{ color: "#dc3545" }}>❌ Non atteint</span>
          ),
      },
      {
        Header: "Détail",
        accessor: "livreur.id",
        Cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => setSelectedLivreur(row.original.livreur)}
          >
            Voir
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const totaux = data?.totaux;
  const livreurs = data?.livreurs ?? [];

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-0">Performance livreurs</h4>
          <small className="text-muted">
            Objectif mensuel par livreur : {fmtEuros(CA_OBJECTIF)}
          </small>
        </Col>
        <Col xs="auto">
          <Form.Control
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ maxWidth: 200 }}
          />
        </Col>
      </Row>

      {/* KPI Cards */}
      {totaux && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={4} xl={2}>
            <KpiCard label="CA total" value={fmtEuros(totaux.ca_ttc)} />
          </Col>
          <Col xs={6} md={4} xl={2}>
            <KpiCard label="Livraisons" value={totaux.nb_livraisons} />
          </Col>
          <Col xs={6} md={4} xl={2}>
            <KpiCard label="Heures totales" value={fmtHeures(totaux.heures)} />
          </Col>
          <Col xs={6} md={4} xl={2}>
            <KpiCard label="Km totaux" value={`${totaux.km} km`} />
          </Col>
          <Col xs={6} md={4} xl={2}>
            <KpiCard label="CA/h moyen" value={fmtEuros(totaux.ca_par_heure_moy)} />
          </Col>
          <Col xs={6} md={4} xl={2}>
            <KpiCard
              label="Objectif 5K atteint"
              value={`${totaux.nb_objectif_ok} / ${totaux.nb_livreurs_total}`}
              highlight={totaux.nb_objectif_ok === totaux.nb_livreurs_total}
              sub={
                totaux.nb_objectif_ok === totaux.nb_livreurs_total
                  ? "Tous à l'objectif ✅"
                  : `${totaux.nb_livreurs_total - totaux.nb_objectif_ok} sous l'objectif`
              }
            />
          </Col>
        </Row>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && <p className="text-muted">Chargement…</p>}

      {/* Tableau */}
      {!loading && (
        <DynamicTable
          columns={columns}
          data={livreurs}
          total={livreurs.length}
          last_page={1}
          nbr={1}
          setNbr={() => {}}
        />
      )}

      {/* Modal détail */}
      <LivreurDetailModal
        livreur={selectedLivreur}
        month={month}
        onHide={() => setSelectedLivreur(null)}
      />
    </Container>
  );
}

export default LivreursPerformance;
